import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { CryptoSalesPanel } from '@/components/crypto/CryptoSalesPanel';
import { useReceipts, useUsers } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useDebounced } from '@/hooks/useDebounced';
import { Avatar, ErrorState, Pagination, StatLine, TableSkeleton } from '@/components/ui';
import { IconPaperclip, IconPlus, IconSearch } from '@/components/Icons';
import { NewReceiptModal } from '@/components/receipts/NewReceiptModal';
import { ReceiptDetailModal } from '@/components/receipts/ReceiptDetailModal';
import { TransferBadge } from '@/components/receipts/TransferBadge';
import { PendingDepositsAlert, RECEIPTS_TAB_KEY, type PendingDepositsState } from '@/components/receipts/PendingDepositsAlert';
import { RejectedSalesAlert } from '@/components/receipts/RejectedSalesAlert';
import {
  formatBRL, formatBRLShort, formatDate, formatNumber, LEVERAGE_LABELS, LEVERAGE_STATUS_LABELS, PERIOD_LABELS, todayISO,
} from '@/lib/format';
import {
  countsAsSold,
  hasOwnValue,
  isReviewed,
  notSoldHint,
  ownValueOf,
  PLATFORM_LABELS,
  RECEIPT_SOURCE_LABELS,
  REVIEW_CHIP,
  REVIEW_HINTS,
  REVIEW_LABELS,
  reviewViewOf,
  soughtOf,
} from '@/lib/receipts';
import type { LeverageStatus, PeriodKey, Receipt, ReceiptSource } from '@/lib/types';

const PERIODS: PeriodKey[] = ['today', 'yesterday', 'week', 'month', 'lastMonth', 'quarter', 'year', 'all'];
const SOURCES: ReceiptSource[] = ['EPHRA', 'CAKTO', 'KIRVANO', 'DOTPAG', 'MUNDPAY', 'CRYPTO', 'MANUAL'];

/** As abas da lista: cada tipo com as próprias colunas. O valor é o filtro de tipo da listagem. */
type TypeTab = '' | 'ALAVANCAGEM' | 'UNA_DEPOSIT' | 'SALE' | 'GAS' | 'LIVE' | 'DEPOSIT';
/**
 * A aba das vendas em cripto não filtra a lista: abre os carnês no lugar dela.
 * Mora na URL (?aba=cripto), não no navegador — quem volta a Comprovantes pelo
 * menu cai na lista, e o voltar do navegador sai dos carnês.
 */
const CRYPTO_TAB = 'CRYPTO';
type Tab = TypeTab | typeof CRYPTO_TAB;
const TABS: { value: Tab; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'ALAVANCAGEM', label: 'Alavancagens' },
  { value: 'UNA_DEPOSIT', label: 'Depósitos UNA Markets' },
  { value: 'SALE', label: 'Vendas' },
  { value: CRYPTO_TAB, label: 'Vendas em cripto' },
  { value: 'GAS', label: 'Gás' },
  { value: 'LIVE', label: 'Repasses Live' },
  { value: 'DEPOSIT', label: 'Depósitos sem tipo' },
];
const TAB_KEY = RECEIPTS_TAB_KEY;

function readTab(): TypeTab {
  try {
    const saved = localStorage.getItem(TAB_KEY);
    return saved !== CRYPTO_TAB && TABS.some((tab) => tab.value === saved) ? (saved as TypeTab) : '';
  } catch {
    return '';
  }
}

function saveTab(tab: TypeTab) {
  try {
    localStorage.setItem(TAB_KEY, tab);
  } catch {
    // Armazenamento bloqueado: a aba vale até recarregar.
  }
}

/** Selo da situação da alavancagem. */
function LeverageStatusBadge({ status }: { status: LeverageStatus | null }) {
  const value = status ?? 'PENDING';
  const tone = value === 'DONE' ? ' chip-positive' : value === 'PENDING' ? ' chip-warning' : ' chip-negative';
  return <span className={`chip${tone}`}>{LEVERAGE_STATUS_LABELS[value]}</span>;
}

/** A alavancagem buscada: só a realizada tem — antes disso, um traço. */
function BuscadaCell({ receipt }: { receipt: Receipt }) {
  const sought = soughtOf(receipt);
  return sought === null ? (
    <td className="num right muted nowrap cell-key" title="A buscada entra quando a alavancagem for realizada">—</td>
  ) : (
    <td className="num right nowrap cell-key" title="Alavancagem buscada: fora do vendido">{formatBRL(sought)}</td>
  );
}

/**
 * Comprovantes: cada venda com a prova anexada. O membro vê e lança os seus;
 * o administrador vê os de todos e filtra por membro.
 */
export function ReceiptsPage() {
  const { isAdmin } = useAuth();

  // Vindo do aviso do painel: direto nos depósitos sem tipo, de todo o período (são pendências, não do mês).
  const fromPendingAlert = Boolean((useLocation().state as PendingDepositsState | null)?.pendingDeposits);
  // A aba das vendas em cripto é a URL: o endereço antigo (/cripto) e o painel chegam com ?aba=cripto.
  const [params, setParams] = useSearchParams();
  const cryptoTab = params.get('aba') === 'cripto';
  const [period, setPeriod] = useState<PeriodKey>(fromPendingAlert ? 'all' : 'month');
  const [from, setFrom] = useState(() => `${todayISO().slice(0, 8)}01`);
  const [to, setTo] = useState(todayISO);
  const [memberId, setMemberId] = useState('');
  const [tab, setTab] = useState<TypeTab>(() => (fromPendingAlert ? 'DEPOSIT' : readTab()));
  const activeTab: Tab = cryptoTab ? CRYPTO_TAB : tab;
  const [leverage, setLeverage] = useState('');
  const [transfer, setTransfer] = useState('');
  const [source, setSource] = useState('');
  /** A conferência do administrador: em conferência, confirmadas, reprovadas. */
  const [review, setReview] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<Receipt | null>(null);

  const debouncedSearch = useDebounced(search);
  const custom = period === 'custom';
  // Datas digitadas ao contrário viram o intervalo certo: a API recusaria e o erro tomaria a página, filtros junto.
  const [start, end] = from && to && from > to ? [to, from] : [from, to];
  const { data: members } = useUsers({}, { enabled: isAdmin });
  // Nos carnês a lista não aparece: a consulta dorme.
  const { data, isLoading, error } = useReceipts({
    ...(custom ? { from: start || undefined, to: end || undefined } : { period }),
    memberId: memberId || undefined,
    leverageType: tab || undefined,
    leverage: tab === 'ALAVANCAGEM' && leverage ? leverage : undefined,
    transfer: transfer || undefined,
    source: source || undefined,
    review: review || undefined,
    search: debouncedSearch || undefined,
    page,
    pageSize: 25,
  }, { enabled: !cryptoTab });

  // Qualquer mudança de filtro volta para a primeira página.
  useEffect(() => {
    setPage(1);
  }, [period, from, to, memberId, tab, leverage, transfer, source, review, debouncedSearch]);

  // No celular as abas rolam de lado: a escolhida — inclusive a que veio por link ou pelo aviso — fica à vista.
  // Só na horizontal: scrollIntoView rolaria a página também.
  const tabsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const list = tabsRef.current;
    const active = list?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!list || !active) return;
    const tabBox = active.getBoundingClientRect();
    const listBox = list.getBoundingClientRect();
    if (tabBox.left < listBox.left || tabBox.right > listBox.right) list.scrollLeft += tabBox.left - listBox.left - 16;
  }, [activeTab]);

  function chooseTab(next: Tab) {
    if (next === CRYPTO_TAB) {
      setParams({ aba: 'cripto' });
      return;
    }
    setTab(next);
    saveTab(next);
    if (cryptoTab) setParams({});
    // Os filtros que só existem em outra aba saem junto: escondidos, continuariam filtrando.
    if (next !== 'ALAVANCAGEM') setLeverage('');
    if (next !== '' && next !== 'SALE') {
      setSource('');
      setReview('');
    }
  }

  /** Setas trocam de aba, como pede o padrão de abas. */
  function onTabKey(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const index = TABS.findIndex((item) => item.value === activeTab);
    const next = TABS[(index + (event.key === 'ArrowRight' ? 1 : -1) + TABS.length) % TABS.length]!.value;
    chooseTab(next);
    document.getElementById(`receipts-tab-${next || 'all'}`)?.focus();
  }

  // O erro da lista não toma os carnês: lá a consulta nem roda, e um erro antigo dela prenderia a pessoa ali.
  if (error && !cryptoTab) return <ErrorState message={(error as Error).message} />;

  /** Na aba de alavancagens, depositado, buscada e a situação da alavancagem ganham colunas próprias. */
  const alavancagens = tab === 'ALAVANCAGEM';
  /** Na aba da UNA Markets, só os números dela: separados da outra corretora e das vendas. */
  const una = tab === 'UNA_DEPOSIT';
  const columns = (isAdmin ? 7 : 6) + (alavancagens ? 2 : 0);
  const summary = data?.summary;
  const filtered = Boolean(memberId || leverage || transfer || source || review || debouncedSearch);
  const emptyMessage = filtered
    ? 'Nenhum comprovante com esses filtros.'
    : `Nenhum comprovante ${period === 'all' ? 'ainda' : 'neste período'}.`;

  return (
    <>
      <h1 className="page-title">Comprovantes</h1>
      <p className="page-lede">
        {isAdmin
          ? 'Depósitos, alavancagens, vendas, gás e repasses de toda a operação, cada um com a prova anexada.'
          : 'Seus depósitos, alavancagens, vendas, gás e repasses, cada um com a prova anexada.'}
      </p>

      <PendingDepositsAlert
        onShow={() => {
          chooseTab('DEPOSIT');
          setPeriod('all');
        }}
      />
      <RejectedSalesAlert
        onShowRejected={() => {
          chooseTab('SALE');
          setReview('rejected');
          setPeriod('all');
        }}
        onShowPending={() => {
          chooseTab('SALE');
          setReview('pending');
          setPeriod('all');
        }}
      />

      <div ref={tabsRef} className="tabs" role="tablist" aria-label="Tipo do comprovante" onKeyDown={onTabKey}>
        {TABS.map((item) => (
          <button
            key={item.value || 'all'}
            type="button"
            role="tab"
            id={`receipts-tab-${item.value || 'all'}`}
            aria-selected={activeTab === item.value}
            aria-controls="receipts-panel"
            tabIndex={activeTab === item.value ? 0 : -1}
            className="tab"
            onClick={() => chooseTab(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {cryptoTab ? (
        <div id="receipts-panel" role="tabpanel" aria-labelledby="receipts-tab-CRYPTO">
          <CryptoSalesPanel />
        </div>
      ) : (
        <>
          <div className="toolbar">
            <div className="search-input toolbar-search">
              <span className="search-input-icon" aria-hidden="true"><IconSearch /></span>
              <input
                className="inp"
                placeholder={isAdmin ? 'Buscar por produto, cliente ou membro' : 'Buscar por produto ou cliente'}
                aria-label="Buscar comprovantes"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <select className="inp" value={period} onChange={(event) => setPeriod(event.target.value as PeriodKey)} aria-label="Período">
              {PERIODS.map((option) => <option key={option} value={option}>{PERIOD_LABELS[option]}</option>)}
              <option value="custom">Personalizado</option>
            </select>

            {custom ? (
              <>
                <input className="inp" style={{ width: 150 }} type="date" value={from} onChange={(event) => setFrom(event.target.value)} aria-label="Data inicial" />
                <span className="muted" style={{ fontSize: 12 }}>até</span>
                <input className="inp" style={{ width: 150 }} type="date" value={to} onChange={(event) => setTo(event.target.value)} aria-label="Data final" />
              </>
            ) : null}

            {isAdmin ? (
              <select className="inp" value={memberId} onChange={(event) => setMemberId(event.target.value)} aria-label="Membro">
                <option value="">Todos os membros</option>
                {members?.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
              </select>
            ) : null}

            {alavancagens ? (
              <select className="inp" value={leverage} onChange={(event) => setLeverage(event.target.value)} aria-label="Alavancagem">
                <option value="">Alavancagem: todas</option>
                <option value="pending">Alavancagem pendente</option>
                <option value="done">Alavancagem realizada</option>
                <option value="failed">Alavancagem não realizada</option>
              </select>
            ) : null}

            {tab === '' || tab === 'SALE' ? (
              <select className="inp" value={source} onChange={(event) => setSource(event.target.value)} aria-label="Origem">
              <option value="">Todas as origens</option>
              {SOURCES.map((option) => <option key={option} value={option}>{RECEIPT_SOURCE_LABELS[option]}</option>)}
              </select>
            ) : null}

            {tab === '' || tab === 'SALE' ? (
              <select className="inp" value={review} onChange={(event) => setReview(event.target.value)} aria-label="Conferência">
                <option value="">Conferência: todas</option>
                <option value="pending">{REVIEW_LABELS.PENDING}</option>
                <option value="linked">Conferidas no checkout</option>
                <option value="approved">Aprovadas sem checkout</option>
                <option value="refunded">Reembolsadas</option>
                <option value="rejected">Reprovadas</option>
              </select>
            ) : null}

            <select className="inp" value={transfer} onChange={(event) => setTransfer(event.target.value)} aria-label="Repasse">
              <option value="">Repasse: todos</option>
              <option value="pending">Repasse pendente</option>
              <option value="done">Repasse feito</option>
              <option value="none">Sem repasse</option>
            </select>

            {/* margin-left:auto em vez de espaçador: se a linha quebrar, a ação principal continua à direita. */}
            <button type="button" className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={() => setCreating(true)}>
              <IconPlus /> Anexar comprovante
            </button>
          </div>

          {summary && una ? (
            // A UNA Markets é corretora à parte: aqui só os números dela.
            <div className="metric-row" style={{ marginBottom: 30 }}>
              <StatLine label="Depósitos UNA Markets" value={formatNumber(summary.count)} />
              <StatLine label="Depositado na UNA" value={formatBRLShort(summary.unaDepositTotal)} color="var(--emphasis)" />
              <StatLine
                label="Repasses pendentes"
                value={formatNumber(summary.pendingTransfers)}
                color={summary.pendingTransfers > 0 ? 'var(--warning)' : undefined}
              />
            </div>
          ) : summary ? (
            <div className="metric-row cols-6" style={{ marginBottom: 30 }}>
              {alavancagens ? (
                // Alavancagem não é vendido: no lugar do vendido (sempre zero aqui), quantas são.
                <StatLine label="Alavancagens" value={formatNumber(summary.count)} />
              ) : (
                <StatLine
                  label="Vendido"
                  value={formatBRLShort(summary.soldTotal)}
                  color="var(--emphasis)"
                  sub={`${formatNumber(summary.count)} ${summary.count === 1 ? 'comprovante' : 'comprovantes'}`}
                />
              )}
              <StatLine label="Depositado" value={formatBRLShort(summary.depositTotal)} />
              <StatLine label="Alavancagem buscada" value={formatBRLShort(summary.leverageTotal)} />
              <StatLine label="Repasse puxado" value={formatBRLShort(summary.transferTotal)} />
              {alavancagens ? (
                <StatLine
                  label="Alavancagens pendentes"
                  value={formatNumber(summary.pendingLeverages)}
                  color={summary.pendingLeverages > 0 ? 'var(--warning)' : undefined}
                />
              ) : (
                <StatLine label="Ticket médio" value={formatBRLShort(summary.averageTicket)} />
              )}
              <StatLine
                label="Repasses pendentes"
                value={formatNumber(summary.pendingTransfers)}
                color={summary.pendingTransfers > 0 ? 'var(--warning)' : undefined}
              />
            </div>
          ) : null}

          {isLoading && !data ? (
            <TableSkeleton rows={8} columns={columns} />
          ) : (
            <>
              <div className="table-wrap" id="receipts-panel" role="tabpanel" aria-labelledby={`receipts-tab-${tab || 'all'}`}>
                <table className="tbl tbl-cards">
                  <thead>
                    <tr>
                      <th className="nowrap">Venda</th>
                      {isAdmin ? <th>Membro</th> : null}
                      <th>Produto</th>
                      <th>Tipo</th>
                      {alavancagens ? (
                        <>
                          <th className="right">Depositado</th>
                          <th className="right">Buscada</th>
                          <th>Alavancagem</th>
                        </>
                      ) : (
                        <th className="right">Valor</th>
                      )}
                      <th>Repasse</th>
                      <th className="right" title="Arquivos anexados" aria-label="Arquivos">
                        <IconPaperclip />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data && data.items.length === 0 ? (
                      <tr>
                        <td colSpan={columns}>
                          <div className="empty">
                            <p style={{ margin: '0 0 14px' }}>{emptyMessage}</p>
                            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                              <IconPlus /> Anexar comprovante
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : null}

                    {data?.items.map((receipt) => (
                      <tr key={receipt.id} className="row-hover row-click" onClick={() => setSelected(receipt)}>
                        <td className="num nowrap" data-label="Venda">
                          {formatDate(receipt.date)} <span className="muted">{receipt.time}</span>
                        </td>
                        {isAdmin ? (
                          <td data-label="Membro">
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                              <Avatar name={receipt.memberName ?? '?'} color={receipt.memberColor} photo={receipt.memberPhotoUrl} size={22} />
                              <span className="nowrap">{receipt.memberName ?? '—'}</span>
                            </span>
                          </td>
                        ) : null}
                        <td className="cell-main">
                          <button
                            type="button"
                            className="link-btn truncate"
                            style={{ display: 'block', maxWidth: 320, fontWeight: 500 }}
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelected(receipt);
                            }}
                          >
                            {receipt.description}
                          </button>
                          {receipt.leadName ? (
                            <div className="muted truncate" style={{ maxWidth: 320, fontSize: 12 }}>{receipt.leadName}</div>
                          ) : null}
                        </td>
                        <td className="nowrap" data-label="Tipo">
                          {receipt.source === 'EPHRA' ? (
                            <span className="chip chip-primary" title="Pedido pago na Ephra pelo link do membro" style={{ marginRight: 6 }}>Ephra</span>
                          ) : PLATFORM_LABELS[receipt.source] ? (
                            <span className="chip chip-primary" title={`Pedido pago na ${PLATFORM_LABELS[receipt.source]}: a venda conta por ele`} style={{ marginRight: 6 }}>{PLATFORM_LABELS[receipt.source]}</span>
                          ) : receipt.source === 'CRYPTO' ? (
                            <span className="chip chip-primary" title="Parcela paga de uma venda em cripto" style={{ marginRight: 6 }}>Cripto</span>
                          ) : null}
                          {isReviewed(receipt) || receipt.adminConfirmed ? (
                            <span
                              className={REVIEW_CHIP[reviewViewOf(receipt)]}
                              title={
                                receipt.reviewStatus === 'REJECTED' && receipt.reviewNote
                                  ? `Reprovada: ${receipt.reviewNote}`
                                  : receipt.adminConfirmed && receipt.reviewNote
                                    ? receipt.reviewNote
                                    : REVIEW_HINTS[reviewViewOf(receipt)]
                              }
                              style={{ marginRight: 6 }}
                            >
                              {REVIEW_LABELS[reviewViewOf(receipt)]}
                            </span>
                          ) : null}
                          {receipt.leverageType === 'DEPOSIT' ? (
                            <span className="chip chip-warning" title="Falta dizer se foi de boas-vindas ou de evento">Depósito · tipo pendente</span>
                          ) : receipt.leverageType ? (
                            <span className="chip">{LEVERAGE_LABELS[receipt.leverageType]}</span>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        {alavancagens ? (
                          <>
                            <td className="num right nowrap" data-label="Depositado">
                              {receipt.depositAmount > 0 ? formatBRL(receipt.depositAmount) : <span className="muted">—</span>}
                            </td>
                            <BuscadaCell receipt={receipt} />
                            <td className="nowrap" data-label="Alavancagem">
                              <LeverageStatusBadge status={receipt.leverageStatus} />
                            </td>
                          </>
                        ) : countsAsSold(receipt.leverageType) ? (
                          <td className="num right emphasis nowrap cell-key" style={{ fontWeight: 500 }}>{formatBRL(receipt.soldAmount)}</td>
                        ) : hasOwnValue(receipt.leverageType) ? (
                          // Depósito, alavancagem (o depositado) e gás mostram o valor na cor do texto: não é vendido.
                          <td className="num right nowrap cell-key" title={notSoldHint(receipt.leverageType)}>{formatBRL(ownValueOf(receipt))}</td>
                        ) : (
                          <td className="num right muted nowrap cell-key" title={notSoldHint(receipt.leverageType)}>—</td>
                        )}
                        <td className="nowrap" data-label="Repasse">
                          <TransferBadge receipt={receipt} withAmount />
                        </td>
                        <td className="num right muted cell-hide-sm">{receipt.attachments.length || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {data ? (
                <Pagination page={data.page} totalPages={data.totalPages} total={data.total} onChange={setPage} />
              ) : null}
            </>
          )}
        </>
      )}

      {creating ? <NewReceiptModal onClose={() => setCreating(false)} /> : null}
      {selected ? <ReceiptDetailModal receipt={selected} onClose={() => setSelected(null)} /> : null}
    </>
  );
}
